import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAccess } from '../../lib/access/access.context';
import { APP_NAVIGATION, visibleNavigation } from '../../lib/navigation';
import type { Permission } from '../../lib/permissions';
import { Alert } from '../ui/alert';

export function AccessRoute({ children }: { children: ReactNode }) {
  const access = useAccess(),
    { pathname, search } = useLocation();
  if (access.error)
    return <Alert {...(access.refresh ? { onRetry: access.refresh } : {})}>{access.error}</Alert>;
  if (!access.data)
    return (
      <p role="status" className="py-8">
        Checking your access…
      </p>
    );
  const flattened = APP_NAVIGATION.flatMap((item) => [item, ...(item.children ?? [])]).filter(
    (item) => !item.children?.length
  );
  const match = flattened
    .filter(
      (item) =>
        pathname === item.path ||
        pathname.startsWith(`${item.path}/`) ||
        item.activePaths?.includes(pathname)
    )
    .sort((a, b) => b.path.length - a.path.length)[0];
  const permissions: readonly Permission[] =
    pathname === '/admin/order-lines' ? ['order-intake:read'] : (match?.requiredPermissions ?? []);
  if (['/', '/admin/dashboard', '/admin/inventory'].includes(pathname)) {
    const first = visibleNavigation(APP_NAVIGATION, access.allows)[0];
    return <Navigate replace to={first?.children?.[0]?.path ?? first?.path ?? '/admin/warnings'} />;
  }
  const needsPrices =
    pathname.startsWith('/admin/delivery-tables') ||
    (pathname === '/admin/procurement' &&
      (!new URLSearchParams(search).get('tab') ||
        new URLSearchParams(search).get('tab') === 'purchase-orders'));
  if (
    pathname === '/admin/procurement' &&
    !new URLSearchParams(search).get('tab') &&
    !new URLSearchParams(search).has('record') &&
    access.allows('procurement:read') &&
    !access.allows('commercial:prices:read')
  )
    return <Navigate replace to="/admin/procurement?tab=allocations" />;
  if (
    !permissions.every(access.allows) ||
    (needsPrices && !access.allows('commercial:prices:read'))
  )
    return (
      <div className="py-8">
        <Alert>You do not have permission to open this workspace.</Alert>
      </div>
    );
  return <>{children}</>;
}
